/// <reference path="../../types/window.d.ts" />
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useQueryParams } from '../../utils/router';

/**
 * @typedef {object} BooleanFlagConfig
 * @property {'boolean'} type
 * @property {boolean} defaultValue
 */

/**
 * @typedef {object} ScopeFlagConfig
 * @property {'scope'} type
 * @property {string} defaultValue
 */

/**
 * @typedef {BooleanFlagConfig | ScopeFlagConfig} FlagConfig
 */

/**
 * @type {{
 *   DisableAuth?: BooleanFlagConfig
 *   Debug?: ScopeFlagConfig
 * }}
 */
const FLAG_CONFIGS = {
  // Only add DisableAuth in development; too risky to have it in production
  ...(process.env.NODE_ENV === 'development'
    ? {
        DisableAuth: {
          type: 'boolean',
          defaultValue: process.env.DISABLE_AUTH === 'true',
        },
      }
    : {}),
  // Debug flag: ''=disabled, 'true'|'*' for all, or scope name like 'cohesion', 'tealium'
  Debug: {
    type: 'scope',
    defaultValue: '',
  },
};

/**
 * @typedef {keyof typeof FLAG_CONFIGS} FlagName
 * @typedef {{ DisableAuth?: boolean; Debug?: string }} FeatureFlags
 */

/** @type {readonly FlagName[]} */
const FLAG_NAMES = /** @type {FlagName[]} */ (Object.keys(FLAG_CONFIGS));

const isTruthy = (v = '') => ['true', '1'].includes(String(v).toLowerCase());

const FeatureFlagsContext = createContext(
  /** @type {FeatureFlags | undefined} */ (undefined),
);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider',
    );
  }
  return context;
};

/**
 * Feature Flag Priority (highest to lowest):
 * 1. URL query (?flag=true) - sets localStorage and takes precedence
 * 2. localStorage - sticky across sessions until cleared
 * 3. Environment variable - fallback default
 *
 * Flags are "sticky" - once set via URL, they persist in localStorage.
 * @param {React.PropsWithChildren<{}>} props
 */
export const FeatureFlagsProvider = ({ children }) => {
  const flagQueryParams = useQueryParams(...FLAG_NAMES);

  const featureFlags = useMemo(() => {
    return /** @type {FeatureFlags} */ (
      Object.fromEntries(
        FLAG_NAMES.map(
          /**
           * @returns {[FlagName, boolean | string]}
           */
          (flag) => {
            const config = FLAG_CONFIGS[flag];
            if (!config) return [flag, false];

            // 1) URL query param (highest priority) + persist
            const queryValue = flagQueryParams[flag];
            if (queryValue !== null) {
              let value;

              if (config.type === 'scope') {
                // For scope flags: "false"/"off"/"" means disabled, anything else is a scope
                value = ['false', 'off', ''].includes(queryValue.toLowerCase())
                  ? ''
                  : queryValue;
              } else {
                // Boolean flags
                value = isTruthy(queryValue);
              }

              try {
                localStorage.setItem(flag, String(value));
              } catch {}
              return [flag, value];
            }

            // 2) localStorage
            try {
              const stored = localStorage.getItem(flag);
              if (stored !== null) {
                if (config.type === 'scope') {
                  return [flag, stored];
                } else {
                  return [flag, isTruthy(stored)];
                }
              }
            } catch {}

            // 3) env default
            return [flag, config.defaultValue];
          },
        ),
      )
    );
  }, [flagQueryParams]);

  useEffect(() => {
    window._featureFlags = featureFlags;

    if (process.env.NODE_ENV === 'development') {
      const enabledFlags = /** @type {[FlagName, boolean | string][]} */ (
        Object.entries(featureFlags).filter(([flagName, value]) => {
          const config = FLAG_CONFIGS[/** @type {FlagName} */ (flagName)];
          if (!config) return false;

          // Scope flags: enabled if non-empty
          if (config.type === 'scope') {
            return value !== '';
          }
          // Boolean flags: enabled if truthy
          return Boolean(value);
        })
      );

      if (enabledFlags.length > 0) {
        console.groupCollapsed(
          `🚩 ${enabledFlags.length} Feature flag(s) active — click for details`,
        );
        console.info('💡 How to disable:');
        enabledFlags.forEach(([flagName, value]) => {
          const config = FLAG_CONFIGS[flagName];

          if (config?.type === 'scope') {
            console.info(`• ${flagName}: "${value}" (scope active)`);
            console.info(
              `  Clear: ?${flagName}= or localStorage.removeItem('${flagName}')`,
            );
          } else {
            console.info(`• ${flagName}: ${value}`);
            console.info(`  Disable: ?${flagName}=false`);
          }
        });
        console.groupEnd();
      } else {
        console.groupCollapsed(
          '🚩 Feature Flags: (none enabled)  — click for tips',
        );
        console.info('💡 Available flags:');
        FLAG_NAMES.forEach((flagName) => {
          const config = FLAG_CONFIGS[flagName];
          if (config?.type === 'scope') {
            console.info(
              `• ?${flagName}=true|cohesion|tealium - scoped logging for specific concerns`,
            );
          } else {
            console.info(`• ?${flagName}=true - enable ${flagName}`);
          }
        });
        console.groupEnd();
      }
    }
  }, [featureFlags]);

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsProvider;
